import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { auditCenterApi } from '@/api/auditCenterApi'
import { useTenant } from '@/providers/TenantProvider'
import { fieldLibraryApi } from '@/api/fieldLibraryApi'
import type { AuditSearchParams } from '@/types/audit'

/**
 * One page of an Audit Trail Center search (z8nrz7dq6q). The tenant is in the
 * key so a switch can never show another tenant's trail; the previous page
 * stays on screen while the next one loads, so paging does not flash empty.
 */
export function useAuditSearch(params: AuditSearchParams, { enabled = true }: { enabled?: boolean } = {}) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['audit-center', selectedTenantId, params],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => auditCenterApi.search(params),
    placeholderData: keepPreviousData,
    // An audit trail is read to find out what happened just now; a five-minute
    // cache would hide the change a reviewer came to check.
    staleTime: 0,
  })
}

/**
 * The search, trended — fetched only while the Trends view is open. One
 * response holds every interval, so the interval is not part of the key and
 * switching it never refetches.
 */
export function useAuditTrends(params: AuditSearchParams, { enabled = true }: { enabled?: boolean } = {}) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['audit-center', 'trends', selectedTenantId, params],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => auditCenterApi.trends(params),
    placeholderData: keepPreviousData,
    staleTime: 0,
  })
}

/**
 * The field library's field names, for the "Field name" picker — the spec's
 * "any field from the field library". Free text still works; this suggests.
 */
export function useFieldLibraryLabels({ enabled = true }: { enabled?: boolean } = {}) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['audit-center', 'field-labels', selectedTenantId],
    enabled: enabled && !!selectedTenantId,
    queryFn: async () => {
      const fields = await fieldLibraryApi.listAllFields()
      return [...new Set(fields.map((f) => f.label).filter((l): l is string => !!l?.trim()))].sort((a, b) =>
        a.localeCompare(b),
      )
    },
    staleTime: 5 * 60 * 1000,
  })
}
